import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { websiteApi } from "../../../api/websiteApi";
import { toast } from "react-toastify";
import "./PackageManager.css";
import "./WebsitePackageManager.css";

const emptyForm = {
  name: "",
  description: "",
  price: "",
  priceLabel: "once-off",
  icon: "fa-globe",
  featuresText: "",
  isPopular: false,
  isQuoteOnly: false,
  isActive: true,
  sortOrder: 1,
};

const WebsitePackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadPackages = async () => {
    setLoading(true);
    setError("");
    try {
      const packageResponse = await websiteApi.getAdminPackages();
      setPackages(packageResponse.data || []);
    } catch (loadError) {
      setError(
        loadError.response?.data?.error || "Failed to load website packages.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId("");
    setShowForm(false);
  };

  const editPackage = (pkg) => {
    setShowForm(true);
    setEditingId(pkg.WebsitePackageID);
    setForm({
      name: pkg.Name || "",
      description: pkg.Description || "",
      price: pkg.Price ?? "",
      priceLabel: pkg.PriceLabel || "",
      icon: pkg.Icon || "fa-globe",
      featuresText: (pkg.Features || []).join("\n"),
      isPopular: Boolean(pkg.IsPopular),
      isQuoteOnly: Boolean(pkg.IsQuoteOnly),
      isActive: Boolean(pkg.IsActive),
      sortOrder: pkg.SortOrder || 1,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      price: form.isQuoteOnly ? null : Number(form.price),
      sortOrder: Number(form.sortOrder),
      features: form.featuresText
        .split("\n")
        .map((feature) => feature.trim())
        .filter(Boolean),
    };
    delete payload.featuresText;

    try {
      if (editingId) {
        await websiteApi.updatePackage(editingId, payload);
        toast.success("Website package updated.");
      } else {
        await websiteApi.createPackage(payload);
        toast.success("Website package created.");
      }
      resetForm();
      await loadPackages();
    } catch (saveError) {
      setError(
        saveError.response?.data?.error || "Failed to save website package.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removePackage = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.Name}"?`)) return;
    setError("");
    try {
      await websiteApi.deletePackage(pkg.WebsitePackageID);
      toast.success("Website package deleted.");
      await loadPackages();
    } catch (deleteError) {
      setError(
        deleteError.response?.data?.error || "Failed to delete website package.",
      );
    }
  };

  return (
    <section className="package-manager website-package-manager">
      <header className="package-page-header">
        <div>
          <div className="package-eyebrow">
            <i className="fas fa-globe" />
            WEBSITE MANAGEMENT
          </div>
          <h2>Website Packages</h2>
          <p>Manage the package information displayed on the public Website page.</p>
        </div>
        <button
          type="button"
          className="package-create-btn"
          onClick={() => {
            if (showForm) {
              resetForm();
              return;
            }
            setEditingId("");
            setForm(emptyForm);
            setShowForm(true);
          }}
        >
          <i className={`fas fa-${showForm ? "times" : "plus"}`} />
          {showForm ? "Cancel" : "New Website Package"}
        </button>
      </header>

      {error && (
        <div className="package-alert package-alert-error">
          <i className="fas fa-exclamation-circle" />
          <span>{error}</span>
        </div>
      )}
      {showForm && (
        <div className="package-form-card">
          <div className="package-form-header">
            <div>
              <span className="package-form-label">WEBSITE PACKAGE CONFIGURATION</span>
              <h3>{editingId ? "Edit Website Package" : "Create Website Package"}</h3>
              <p>Configure the package details shown on the public Website page.</p>
            </div>
            <div className="package-form-icon">
              <i className="fas fa-sliders-h" />
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="package-form-grid">
              <div className="package-field">
                <label htmlFor="website-package-name">Package Name</label>
                <input
                  id="website-package-name"
                  required
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="package-field">
                <label htmlFor="website-package-icon">Icon Class</label>
                <input
                  id="website-package-icon"
                  value={form.icon}
                  onChange={(event) => setForm({ ...form, icon: event.target.value })}
                  placeholder="fa-globe"
                />
              </div>
              <div className="package-field package-field-full">
                <label htmlFor="website-package-description">Description</label>
                <input
                  id="website-package-description"
                  value={form.description}
                  onChange={(event) =>
                    setForm({ ...form, description: event.target.value })
                  }
                />
              </div>
              <div className="package-field">
                <label htmlFor="website-package-price">Price (ZAR)</label>
                <div className="package-input-prefix">
                  <span>R</span>
                  <input
                    id="website-package-price"
                    type="number"
                    min="0"
                    step="0.01"
                    required={!form.isQuoteOnly}
                    disabled={form.isQuoteOnly}
                    value={form.price}
                    onChange={(event) => setForm({ ...form, price: event.target.value })}
                  />
                </div>
              </div>
              <div className="package-field">
                <label htmlFor="website-package-price-label">Price Label</label>
                <input
                  id="website-package-price-label"
                  value={form.priceLabel}
                  disabled={form.isQuoteOnly}
                  onChange={(event) =>
                    setForm({ ...form, priceLabel: event.target.value })
                  }
                  placeholder="once-off"
                />
              </div>
              <div className="package-field">
                <label htmlFor="website-package-sort">Display Order</label>
                <input
                  id="website-package-sort"
                  type="number"
                  min="0"
                  value={form.sortOrder}
                  onChange={(event) =>
                    setForm({ ...form, sortOrder: event.target.value })
                  }
                />
              </div>
              <div className="package-field package-field-full">
                <label htmlFor="website-package-features">Included Features (one per line)</label>
                <textarea
                  id="website-package-features"
                  rows={7}
                  value={form.featuresText}
                  onChange={(event) =>
                    setForm({ ...form, featuresText: event.target.value })
                  }
                />
              </div>
            </div>
            <div className="package-active-toggle website-package-toggles">
              <label>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm({ ...form, isActive: event.target.checked })
                  }
                />
                <span className="package-toggle" />
                <span><strong>Package Active</strong><small>Show this package on the public website.</small></span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.isPopular}
                  onChange={(event) =>
                    setForm({ ...form, isPopular: event.target.checked })
                  }
                />
                <span className="package-toggle" />
                <span><strong>Most Popular</strong><small>Highlight this package on the website.</small></span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.isQuoteOnly}
                  onChange={(event) =>
                    setForm({ ...form, isQuoteOnly: event.target.checked })
                  }
                />
                <span className="package-toggle" />
                <span><strong>Custom Quote</strong><small>Show this package as a quote-based offer.</small></span>
              </label>
            </div>
            <div className="package-form-actions">
              <button
                type="button"
                className="package-cancel-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button type="submit" className="package-save-btn" disabled={saving}>
                <i className="fas fa-save" />
                {saving ? "Saving..." : editingId ? "Update Package" : "Create Package"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="package-list-header">
        <div>
          <h3>Available Website Packages</h3>
          <span>{packages.length} package{packages.length !== 1 ? "s" : ""} configured</span>
        </div>
        <div className="package-list-count">
          <i className="fas fa-layer-group" />
          {packages.length}
        </div>
      </div>
      {loading ? (
        <div className="package-empty">
          <div className="package-empty-icon"><i className="fas fa-spinner fa-spin" /></div>
          <h3>Loading website packages...</h3>
        </div>
      ) : (
        <div className="package-grid website-admin-package-grid">
          {packages.length === 0 ? (
            <div className="package-empty">
              <div className="package-empty-icon"><i className="fas fa-globe" /></div>
              <h3>No website packages yet</h3>
              <p>Create a package to display it on the public Website page.</p>
              <button
                type="button"
                className="package-create-btn"
                onClick={() => setShowForm(true)}
              >
                <i className="fas fa-plus" /> Create Website Package
              </button>
            </div>
          ) : (
            packages.map((pkg) => (
              <article
                className={`package-card website-admin-package-card ${pkg.IsActive ? "" : "package-card-inactive"} ${pkg.IsPopular ? "popular" : ""}`}
                key={pkg.WebsitePackageID}
              >
                <div className="package-card-top">
                  <div className="package-tier-icon"><i className={`fas ${pkg.Icon || "fa-globe"}`} /></div>
                  <div className="package-tier-info">
                    <span>{pkg.IsQuoteOnly ? "Custom Quote" : "Website"}</span>
                    <h3>{pkg.Name}</h3>
                  </div>
                  <span className={`package-status ${pkg.IsActive ? "package-status-active" : "package-status-inactive"}`}>
                    <span />{pkg.IsActive ? "Active" : "Inactive"}
                  </span>
                </div>
                {pkg.IsPopular && <div className="package-popular-badge">MOST POPULAR</div>}
                <p className="website-admin-package-description">{pkg.Description}</p>
                <div className="package-price">
                  {pkg.IsQuoteOnly ? (
                    <strong>Custom Quote</strong>
                  ) : (
                    <>
                      <span className="package-currency">R</span>
                      <strong>{Number(pkg.Price || 0).toLocaleString("en-ZA")}</strong>
                      <span className="package-period">/ {pkg.PriceLabel || "once-off"}</span>
                    </>
                  )}
                </div>
                <div className="package-features">
                  <div className="package-features-title">Included Features</div>
                  {(pkg.Features || []).length ? (
                    pkg.Features.map((feature) => (
                      <div className="package-feature" key={feature}>
                        <span className="package-feature-check"><i className="fas fa-check" /></span>
                        <small>{feature}</small>
                      </div>
                    ))
                  ) : (
                    <div className="package-no-features">No features configured.</div>
                  )}
                </div>
                <div className="package-card-actions">
                  <button type="button" className="package-edit-btn" onClick={() => editPackage(pkg)}>
                    <i className="fas fa-pen" /> Edit
                  </button>
                  <button
                    type="button"
                    className="package-delete-btn"
                    onClick={() => removePackage(pkg)}
                    aria-label={`Delete ${pkg.Name}`}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      )}

      <section className="website-request-admin-list">
        <div>
          <h2>Website request operations</h2>
          <p>Review submissions, manage request status, and prepare custom quotes from the Operations workspace.</p>
        </div>
        <Link to="/admin/website-requests" className="website-request-operations-link">
          Open website requests <i className="fas fa-arrow-right" />
        </Link>
      </section>
    </section>
  );
};

export default WebsitePackageManager;
